import AppContext from 'App/AppContext';
import {
  CopyTaskViewSource,
  ICreateTaskViewInput,
  ITaskViewStateContextValue,
  TaskViewStateContext,
} from 'components/Tasks/TaskViews/TaskViewStateContext';
import {
  FilterSaveState,
  ITaskCustomView,
  ITaskViewLayoutReducerState,
  ITaskViewLayoutState,
  TaskViewEditableFields,
  TaskViewLayoutAction,
} from 'components/Tasks/TaskViews/TaskViewTypes';
import { TaskViewCopyDTO, TaskViewDTO } from 'models/dto/tasks/taskViewDTO';
import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCopyTaskView, apiCreateTaskView, apiDeleteTaskView } from 'services/Api/taskViewService';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { logTaskViewDiagnostic } from 'utils/taskViewDiagnostics';
import { canUseTaskViews } from './TaskViewAuthorization';
import { isSourceTemplateAllowed, myTaskKey, routeToSourceKey, sanitizeCustomViews } from './TaskViewHelpers';
import { TaskViewEventHandler, TaskViewResyncHandler, useTaskViewHub } from './TaskViewHubProvider';
import { createTaskViewLayoutState } from './TaskViewLayoutModel';
import { TaskViewSaveQueue } from './TaskViewSaveQueue';
import { getCustomViewRouteId, getCustomViewUrl } from './TaskViewUtils';
import { getTaskViewLayoutScopeKey, useTaskViewLayoutSync } from './useTaskViewLayoutSync';

const viewKeyPrefix = 'view-api-';
const draftKeyPrefix = 'view-creating-';

const buildViewKey = (taskViewId: number): string => `${viewKeyPrefix}${taskViewId}`;

// Key of the layout chip in the save-state map, next to the view keys.
const layoutSaveStateKey = 'layout';
const saveStateResetMs: Partial<Record<FilterSaveState, number>> = { saved: 750, error: 3000 };

const withSaveState = (
  states: Record<string, FilterSaveState>,
  key: string,
  state: FilterSaveState,
): Record<string, FilterSaveState> => {
  if ((states[key] ?? 'idle') === state) return states;
  const next = { ...states };
  if (state === 'idle') {
    delete next[key];
  } else {
    next[key] = state;
  }

  return next;
};

// Own views need a source the user may use. Views shared by others always show.
const isViewVisible = (dto: TaskViewDTO, isManager: boolean): boolean =>
  (dto.isShared && dto.ownedByMe === false) || isSourceTemplateAllowed(routeToSourceKey(dto.sourceRoute), isManager);

const findTaskViewId = (views: ITaskCustomView[], viewKey: string): number | undefined =>
  views.find((v) => v.key === viewKey)?.taskViewId;

// The view as the server has it, with this tab's unsaved edits on top.
const toCustomView = (dto: TaskViewDTO, edits: TaskViewEditableFields, isManager: boolean): ITaskCustomView => {
  const label = edits.label ?? dto.name;
  const sourceKey = routeToSourceKey(dto.sourceRoute);

  return {
    key: buildViewKey(dto.taskViewId),
    label,
    url: getCustomViewUrl(`${dto.taskViewId}`, label),
    sourceTemplateKey: isSourceTemplateAllowed(sourceKey, isManager) ? sourceKey : myTaskKey,
    filterValues: [...(edits.filterValues ?? dto.filterValues ?? [])],
    taskViewId: dto.taskViewId,
    isShared: dto.isShared,
    ownedByMe: dto.ownedByMe,
    parentViewId: dto.parentViewId,
  };
};

const createInitialReducerState = (): ITaskViewLayoutReducerState => ({
  doc: createTaskViewLayoutState(),
  mutationSeq: 0,
  dirty: false,
});

// Single source of truth for the task-view layout. EDIT applies a local change
// (a user action, a SignalR mutation) and sets `dirty`; APPLY swaps the whole
// layout at once (a server load, a scope reset) and clears `dirty`; MARK_SAVED
// clears `dirty` after a confirmed PUT. Either way one dispatch == one render,
// so the layout save-effect can never observe a half-applied layout -- which
// is what caused the cross-tab flicker loop.
const taskViewLayoutReducer = (
  state: ITaskViewLayoutReducerState,
  action: TaskViewLayoutAction,
): ITaskViewLayoutReducerState => {
  switch (action.type) {
    case 'EDIT': {
      const nextDoc = action.update(state.doc);

      return nextDoc === state.doc ? state : { doc: nextDoc, mutationSeq: state.mutationSeq + 1, dirty: true };
    }
    case 'APPLY':
      return { doc: action.layout, mutationSeq: state.mutationSeq + 1, dirty: false };
    case 'MARK_SAVED':
      return state.dirty && state.mutationSeq === action.mutationSeq ? { ...state, dirty: false } : state;
    default:
      return state;
  }
};

const sameStringArray = (left: string[] | undefined, right: string[] | undefined): boolean => {
  const leftValues = left ?? [];
  const rightValues = right ?? [];

  return leftValues.length === rightValues.length && leftValues.every((value, index) => value === rightValues[index]);
};

const addLayoutKey = (keys: string[], key: string): string[] => {
  if (!key) return keys;

  return keys.includes(key) ? keys : [...keys, key];
};

const removeLayoutKey = (keys: string[], key: string): string[] => {
  if (!key) return keys;

  return keys.includes(key) ? keys.filter((item) => item !== key) : keys;
};

const setMemberFilterEntry = (
  map: Record<string, string[]>,
  viewKey: string,
  filter: string[] | undefined,
): Record<string, string[]> => {
  const nextFilter = filter && filter.length > 0 ? [...filter] : undefined;
  const currentFilter = map[viewKey];
  if (sameStringArray(currentFilter, nextFilter)) return map;

  const nextMap = { ...map };
  if (nextFilter) {
    nextMap[viewKey] = nextFilter;
  } else {
    delete nextMap[viewKey];
  }

  return nextMap;
};

const setStandardFilterOverrideEntry = (
  map: Record<string, string[]>,
  viewKey: string,
  filter: string[] | undefined,
): Record<string, string[]> => {
  if (filter === undefined) {
    return removeFilterMapEntry(map, viewKey);
  }

  const nextFilter = [...filter];
  const currentFilter = map[viewKey];
  if (viewKey in map && sameStringArray(currentFilter, nextFilter)) return map;

  return { ...map, [viewKey]: nextFilter };
};

const removeFilterMapEntry = (map: Record<string, string[]>, viewKey: string): Record<string, string[]> => {
  if (!(viewKey in map)) return map;
  const nextMap = { ...map };
  delete nextMap[viewKey];

  return nextMap;
};

const moveFilterMapEntry = (
  map: Record<string, string[]>,
  previousViewKey: string | undefined,
  viewKey: string,
): Record<string, string[]> => {
  if (!previousViewKey || !(previousViewKey in map)) return map;
  const nextMap = { ...map };
  nextMap[viewKey] = [...map[previousViewKey]];
  delete nextMap[previousViewKey];

  return nextMap;
};

interface IProps {
  children?: ReactNode;
}

interface ITaskViewProviderState {
  customViews: ITaskCustomView[];
  selectedTaskViewKey: string | undefined;
  isLayoutLoading: boolean;
}

const createInitialProviderState = (): ITaskViewProviderState => ({
  customViews: [],
  selectedTaskViewKey: undefined,
  isLayoutLoading: true,
});

// React state setters accept either a concrete next value or an updater
// function. The provider stores several fields in one object, so this unwraps
// that SetStateAction shape before the field-specific setter patches the
// provider object.
const resolveStateAction = <T,>(action: React.SetStateAction<T>, previous: T): T => {
  if (typeof action === 'function') {
    return (action as (value: T) => T)(previous);
  }

  return action;
};

// Owns TaskView layout state (view order, hidden keys, member filters,
// standard-view filter overrides and the custom-view list) in memory. Mounted
// app-wide so both the sidebar (NavMain) and the Tasks scene (TasksOverview)
// read and write one shared source of truth. The server stays canonical: the
// provider loads from the API on mount and on a tenant/user scope change,
// persists through useTaskViewLayoutSync, and refreshes the same state on
// SignalR events.
export const TaskViewStateProvider: FC<IProps> = ({ children }) => {
  const { t } = useTranslation(['tasks']);
  const appContext = useContext(AppContext);
  const isManager = hasUserFeatureGenericManager(appContext);
  const scopeKey = getTaskViewLayoutScopeKey(appContext);
  const taskViewsAvailable = canUseTaskViews(appContext);

  const [providerState, setProviderState] = useState<ITaskViewProviderState>(createInitialProviderState);
  const { customViews, selectedTaskViewKey, isLayoutLoading } = providerState;

  // The one write path for customViews. The ref is written synchronously,
  // BEFORE the state update is scheduled, so a save task that runs in the same
  // tick as an edit (rename + flush) reads the edit -- a ref filled from
  // render would still hold the previous list until React re-renders.
  const customViewsRef = useRef<ITaskCustomView[]>(customViews);
  const commitCustomViews = useCallback((updater: (views: ITaskCustomView[]) => ITaskCustomView[]) => {
    const next = updater(customViewsRef.current);
    if (next === customViewsRef.current) return;
    customViewsRef.current = next;
    setProviderState((state) => (state.customViews === next ? state : { ...state, customViews: next }));
  }, []);

  const setSelectedTaskViewKey = useCallback<React.Dispatch<React.SetStateAction<string | undefined>>>((action) => {
    setProviderState((state) => {
      const selectedTaskViewKeyNext = resolveStateAction(action, state.selectedTaskViewKey);

      return selectedTaskViewKeyNext === state.selectedTaskViewKey
        ? state
        : { ...state, selectedTaskViewKey: selectedTaskViewKeyNext };
    });
  }, []);

  const setIsLayoutLoading = useCallback((isLayoutLoadingNext: boolean) => {
    setProviderState((state) =>
      state.isLayoutLoading === isLayoutLoadingNext ? state : { ...state, isLayoutLoading: isLayoutLoadingNext },
    );
  }, []);

  // The whole layout is one reducer-managed object so a load applies in one
  // atomic update.
  const [layoutState, dispatchLayout] = useReducer(taskViewLayoutReducer, undefined, createInitialReducerState);
  const layoutDoc = layoutState.doc;
  const {
    viewOrder,
    hiddenTemplateViewKeys,
    hiddenCustomViewKeys,
    hiddenSidebarViewKeys,
    viewMemberFilters,
    standardViewFilterOverrides,
  } = layoutDoc;
  const replaceViewOrder = useCallback((nextViewOrder: string[]) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        return sameStringArray(state.viewOrder, nextViewOrder) ? state : { ...state, viewOrder: [...nextViewOrder] };
      },
    });
  }, []);

  const pruneLayoutKeys = useCallback((templateKeys: string[], customKeys: string[], sidebarKeys: string[]) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        const templateKeySet = new Set(templateKeys);
        const customKeySet = new Set(customKeys);
        const sidebarKeySet = new Set(sidebarKeys);
        const hiddenTemplateViewKeysNext = [
          ...new Set(state.hiddenTemplateViewKeys.filter((key) => templateKeySet.has(key))),
        ];
        const hiddenCustomViewKeysNext = [
          ...new Set(state.hiddenCustomViewKeys.filter((key) => customKeySet.has(key) || key.startsWith('view-api-'))),
        ];
        const hiddenSidebarViewKeysNext = [
          ...new Set(
            state.hiddenSidebarViewKeys.filter((key) => sidebarKeySet.has(key) || key.startsWith('view-api-')),
          ),
        ];
        if (
          sameStringArray(state.hiddenTemplateViewKeys, hiddenTemplateViewKeysNext) &&
          sameStringArray(state.hiddenCustomViewKeys, hiddenCustomViewKeysNext) &&
          sameStringArray(state.hiddenSidebarViewKeys, hiddenSidebarViewKeysNext)
        ) {
          return state;
        }

        logTaskViewDiagnostic('state.layout.pruneUnknown', {
          hiddenTemplateViewKeys: hiddenTemplateViewKeysNext,
          hiddenCustomViewKeys: hiddenCustomViewKeysNext,
          hiddenSidebarViewKeys: hiddenSidebarViewKeysNext,
        });

        return {
          ...state,
          hiddenTemplateViewKeys: hiddenTemplateViewKeysNext,
          hiddenCustomViewKeys: hiddenCustomViewKeysNext,
          hiddenSidebarViewKeys: hiddenSidebarViewKeysNext,
        };
      },
    });
  }, []);

  const showViewInTabBar = useCallback((viewKey: string) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        const hiddenTemplateViewKeysNext = removeLayoutKey(state.hiddenTemplateViewKeys, viewKey);
        const hiddenCustomViewKeysNext = removeLayoutKey(state.hiddenCustomViewKeys, viewKey);
        const viewOrderNext = addLayoutKey(state.viewOrder, viewKey);
        if (
          hiddenTemplateViewKeysNext === state.hiddenTemplateViewKeys &&
          hiddenCustomViewKeysNext === state.hiddenCustomViewKeys &&
          viewOrderNext === state.viewOrder
        ) {
          return state;
        }

        return {
          ...state,
          hiddenTemplateViewKeys: hiddenTemplateViewKeysNext,
          hiddenCustomViewKeys: hiddenCustomViewKeysNext,
          viewOrder: viewOrderNext,
        };
      },
    });
  }, []);

  const showCreatedCustomViewInTabBar = useCallback((viewKey: string, previousViewKey?: string) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        const hiddenCustomViewKeysNext = removeLayoutKey(
          removeLayoutKey(state.hiddenCustomViewKeys, viewKey),
          previousViewKey ?? '',
        );
        const hiddenSidebarViewKeysNext = addLayoutKey(
          previousViewKey
            ? state.hiddenSidebarViewKeys.filter((key) => key !== previousViewKey)
            : state.hiddenSidebarViewKeys,
          viewKey,
        );
        const viewOrderNext =
          previousViewKey && state.viewOrder.includes(previousViewKey)
            ? state.viewOrder.map((key) => (key === previousViewKey ? viewKey : key))
            : addLayoutKey(state.viewOrder, viewKey);
        const viewMemberFiltersNext = moveFilterMapEntry(state.viewMemberFilters, previousViewKey, viewKey);
        const standardViewFilterOverridesNext = previousViewKey
          ? removeFilterMapEntry(state.standardViewFilterOverrides, previousViewKey)
          : state.standardViewFilterOverrides;
        if (
          hiddenCustomViewKeysNext === state.hiddenCustomViewKeys &&
          hiddenSidebarViewKeysNext === state.hiddenSidebarViewKeys &&
          sameStringArray(viewOrderNext, state.viewOrder) &&
          viewMemberFiltersNext === state.viewMemberFilters &&
          standardViewFilterOverridesNext === state.standardViewFilterOverrides
        ) {
          return state;
        }

        return {
          ...state,
          hiddenCustomViewKeys: hiddenCustomViewKeysNext,
          hiddenSidebarViewKeys: hiddenSidebarViewKeysNext,
          viewOrder: viewOrderNext,
          viewMemberFilters: viewMemberFiltersNext,
          standardViewFilterOverrides: standardViewFilterOverridesNext,
        };
      },
    });
  }, []);

  const hideViewEverywhere = useCallback((viewKey: string, isTemplateView: boolean) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        const hiddenSidebarViewKeysNext = addLayoutKey(state.hiddenSidebarViewKeys, viewKey);
        const hiddenTemplateViewKeysNext = isTemplateView
          ? addLayoutKey(state.hiddenTemplateViewKeys, viewKey)
          : state.hiddenTemplateViewKeys;
        const hiddenCustomViewKeysNext = isTemplateView
          ? state.hiddenCustomViewKeys
          : addLayoutKey(state.hiddenCustomViewKeys, viewKey);
        if (
          hiddenSidebarViewKeysNext === state.hiddenSidebarViewKeys &&
          hiddenTemplateViewKeysNext === state.hiddenTemplateViewKeys &&
          hiddenCustomViewKeysNext === state.hiddenCustomViewKeys
        ) {
          return state;
        }

        return {
          ...state,
          hiddenSidebarViewKeys: hiddenSidebarViewKeysNext,
          hiddenTemplateViewKeys: hiddenTemplateViewKeysNext,
          hiddenCustomViewKeys: hiddenCustomViewKeysNext,
        };
      },
    });
  }, []);

  // Bulk version of hideViewEverywhere. One EDIT for the whole selection so the
  // save-effect sees a single final layout. Callers split their selection into
  // template vs custom keys because the two go into different hidden lists.
  const hideManyViewsEverywhere = useCallback((templateViewKeys: string[], customViewKeys: string[]) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        let hiddenSidebarViewKeysNext = state.hiddenSidebarViewKeys;
        for (const key of [...templateViewKeys, ...customViewKeys]) {
          hiddenSidebarViewKeysNext = addLayoutKey(hiddenSidebarViewKeysNext, key);
        }
        let hiddenTemplateViewKeysNext = state.hiddenTemplateViewKeys;
        for (const key of templateViewKeys) {
          hiddenTemplateViewKeysNext = addLayoutKey(hiddenTemplateViewKeysNext, key);
        }
        let hiddenCustomViewKeysNext = state.hiddenCustomViewKeys;
        for (const key of customViewKeys) {
          hiddenCustomViewKeysNext = addLayoutKey(hiddenCustomViewKeysNext, key);
        }
        if (
          hiddenSidebarViewKeysNext === state.hiddenSidebarViewKeys &&
          hiddenTemplateViewKeysNext === state.hiddenTemplateViewKeys &&
          hiddenCustomViewKeysNext === state.hiddenCustomViewKeys
        ) {
          return state;
        }

        return {
          ...state,
          hiddenSidebarViewKeys: hiddenSidebarViewKeysNext,
          hiddenTemplateViewKeys: hiddenTemplateViewKeysNext,
          hiddenCustomViewKeys: hiddenCustomViewKeysNext,
        };
      },
    });
  }, []);

  const setViewSidebarVisibility = useCallback((viewKey: string, showInSidebar: boolean, previousViewKey?: string) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        const withoutPrevious = previousViewKey
          ? state.hiddenSidebarViewKeys.filter((key) => key !== previousViewKey)
          : state.hiddenSidebarViewKeys;
        const hiddenSidebarViewKeysNext = showInSidebar
          ? removeLayoutKey(withoutPrevious, viewKey)
          : addLayoutKey(withoutPrevious, viewKey);

        return hiddenSidebarViewKeysNext === state.hiddenSidebarViewKeys
          ? state
          : { ...state, hiddenSidebarViewKeys: hiddenSidebarViewKeysNext };
      },
    });
  }, []);

  // Bulk version of setViewSidebarVisibility. One EDIT for the whole selection.
  const setManyViewsSidebarVisibility = useCallback((viewKeys: string[], showInSidebar: boolean) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        let hiddenSidebarViewKeysNext = state.hiddenSidebarViewKeys;
        for (const key of viewKeys) {
          hiddenSidebarViewKeysNext = showInSidebar
            ? removeLayoutKey(hiddenSidebarViewKeysNext, key)
            : addLayoutKey(hiddenSidebarViewKeysNext, key);
        }

        return hiddenSidebarViewKeysNext === state.hiddenSidebarViewKeys
          ? state
          : { ...state, hiddenSidebarViewKeys: hiddenSidebarViewKeysNext };
      },
    });
  }, []);

  const removeViewFromLayout = useCallback((viewKey: string) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        const viewOrderNext = removeLayoutKey(state.viewOrder, viewKey);
        const hiddenTemplateViewKeysNext = removeLayoutKey(state.hiddenTemplateViewKeys, viewKey);
        const hiddenCustomViewKeysNext = removeLayoutKey(state.hiddenCustomViewKeys, viewKey);
        const hiddenSidebarViewKeysNext = removeLayoutKey(state.hiddenSidebarViewKeys, viewKey);
        const viewMemberFiltersNext = removeFilterMapEntry(state.viewMemberFilters, viewKey);
        const standardViewFilterOverridesNext = removeFilterMapEntry(state.standardViewFilterOverrides, viewKey);
        if (
          viewOrderNext === state.viewOrder &&
          hiddenTemplateViewKeysNext === state.hiddenTemplateViewKeys &&
          hiddenCustomViewKeysNext === state.hiddenCustomViewKeys &&
          hiddenSidebarViewKeysNext === state.hiddenSidebarViewKeys &&
          viewMemberFiltersNext === state.viewMemberFilters &&
          standardViewFilterOverridesNext === state.standardViewFilterOverrides
        ) {
          return state;
        }

        return {
          ...state,
          viewOrder: viewOrderNext,
          hiddenTemplateViewKeys: hiddenTemplateViewKeysNext,
          hiddenCustomViewKeys: hiddenCustomViewKeysNext,
          hiddenSidebarViewKeys: hiddenSidebarViewKeysNext,
          viewMemberFilters: viewMemberFiltersNext,
          standardViewFilterOverrides: standardViewFilterOverridesNext,
        };
      },
    });
  }, []);

  const setViewMemberFilter = useCallback((viewKey: string, filter: string[] | undefined) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        const viewMemberFiltersNext = setMemberFilterEntry(state.viewMemberFilters, viewKey, filter);

        return viewMemberFiltersNext === state.viewMemberFilters
          ? state
          : { ...state, viewMemberFilters: viewMemberFiltersNext };
      },
    });
  }, []);
  const setStandardViewFilterOverride = useCallback((viewKey: string, filter: string[] | undefined) => {
    dispatchLayout({
      type: 'EDIT',
      update: (state) => {
        const standardViewFilterOverridesNext = setStandardFilterOverrideEntry(
          state.standardViewFilterOverrides,
          viewKey,
          filter,
        );

        return standardViewFilterOverridesNext === state.standardViewFilterOverrides
          ? state
          : { ...state, standardViewFilterOverrides: standardViewFilterOverridesNext };
      },
    });
  }, []);
  // Applies a whole layout atomically -- used by the server-load path so the
  // save-effect sees one final layout, never a partial one. Clears `dirty`.
  const applyRemoteLayout = useCallback((next: ITaskViewLayoutState) => {
    dispatchLayout({ type: 'APPLY', layout: next });
  }, []);

  // Confirms a save -- clears `dirty` unless an edit happened during the PUT.
  const markLayoutSaved = useCallback((mutationSeq: number) => {
    dispatchLayout({ type: 'MARK_SAVED', mutationSeq });
  }, []);

  // Refs read by loadTaskViews and the save queue so they stay identity-stable
  // across appContext value churn (AppContextProvider re-creates its value on
  // every window resize).
  const appContextRef = useRef(appContext);
  appContextRef.current = appContext;
  const taskViewsCacheRef = useRef(appContext.globalDataCache.taskViews);
  taskViewsCacheRef.current = appContext.globalDataCache.taskViews;
  const currentScopeKeyRef = useRef<string>(scopeKey);
  currentScopeKeyRef.current = scopeKey;
  const isManagerRef = useRef(isManager);
  isManagerRef.current = isManager;
  const tRef = useRef(t);
  tRef.current = t;
  // Ids this tab removed (own delete, viewDeleted event, 404 on save) mapped
  // to WHEN the removal was confirmed by the server (Infinity while an own
  // optimistic delete is still in flight). A GET that started before the
  // removal must not put the view back, and neither may an echo -- but a load
  // that STARTED after a confirmed removal and still lists the row is server
  // truth (an unshared view was re-shared) and clears the suppression.
  const deletedViewIdsRef = useRef(new Map<number, number>());

  // The per-view chip and the layout chip. 'saved' and 'error' clear themselves,
  // so switching to a view that saved a while ago never shows a stale "Saved".
  const [saveStates, setSaveStates] = useState<Record<string, FilterSaveState>>({});
  const saveStateTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const setSaveState = useCallback((key: string, state: FilterSaveState) => {
    const timers = saveStateTimersRef.current;
    clearTimeout(timers.get(key));
    timers.delete(key);
    setSaveStates((states) => withSaveState(states, key, state));

    const resetMs = saveStateResetMs[state];
    if (resetMs === undefined) return;
    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key);
        setSaveStates((states) => (states[key] === state ? withSaveState(states, key, 'idle') : states));
      }, resetMs),
    );
  }, []);
  const setLayoutSaveState = useCallback(
    (state: FilterSaveState) => setSaveState(layoutSaveStateKey, state),
    [setSaveState],
  );

  // Removes a view that no longer exists on the server (viewDeleted event, a
  // 404 on save, the tab's own delete) from the list, the cache, every layout
  // field and the selection.
  const removeViewLocally = useCallback(
    (taskViewId: number, serverConfirmed: boolean = true) => {
      const key = buildViewKey(taskViewId);
      deletedViewIdsRef.current.set(taskViewId, serverConfirmed ? Date.now() : Number.POSITIVE_INFINITY);
      commitCustomViews((views) =>
        views.some((v) => v.taskViewId === taskViewId) ? views.filter((v) => v.taskViewId !== taskViewId) : views,
      );
      const cache = taskViewsCacheRef.current;
      if (cache.has(taskViewId)) {
        cache.remove(cache.get(taskViewId));
      }
      // Drop the deleted view from every layout field in one atomic edit.
      // Bail (no dirty, no save) when the view was not in this layout.
      dispatchLayout({
        type: 'EDIT',
        update: (doc) => {
          const viewOrderNext = doc.viewOrder.filter((k) => k !== key);
          const hiddenCustomNext = doc.hiddenCustomViewKeys.filter((k) => k !== key);
          const hiddenSidebarNext = doc.hiddenSidebarViewKeys.filter((k) => k !== key);
          const viewMemberFiltersNext = removeFilterMapEntry(doc.viewMemberFilters, key);
          const standardViewFilterOverridesNext = removeFilterMapEntry(doc.standardViewFilterOverrides, key);
          if (
            viewOrderNext.length === doc.viewOrder.length &&
            hiddenCustomNext.length === doc.hiddenCustomViewKeys.length &&
            hiddenSidebarNext.length === doc.hiddenSidebarViewKeys.length &&
            viewMemberFiltersNext === doc.viewMemberFilters &&
            standardViewFilterOverridesNext === doc.standardViewFilterOverrides
          ) {
            return doc;
          }

          return {
            ...doc,
            viewOrder: viewOrderNext,
            hiddenCustomViewKeys: hiddenCustomNext,
            hiddenSidebarViewKeys: hiddenSidebarNext,
            viewMemberFilters: viewMemberFiltersNext,
            standardViewFilterOverrides: standardViewFilterOverridesNext,
          };
        },
      });
      setSelectedTaskViewKey((prev) => (prev === key ? undefined : prev));
      setSaveState(key, 'idle');
    },
    [commitCustomViews, setSelectedTaskViewKey, setSaveState],
  );

  // Owns the server version and the unsaved edits of every saved view. The
  // provider only mirrors what it reports into React state.
  const [saveQueue] = useState(
    () =>
      new TaskViewSaveQueue({
        getAccessToken: () => appContextRef.current.getAccessToken(apiRequest.scopes),
        renderView: (server, edits) => {
          commitCustomViews((views) =>
            views.some((v) => v.taskViewId === server.taskViewId)
              ? views.map((v) =>
                  v.taskViewId === server.taskViewId ? toCustomView(server, edits, isManagerRef.current) : v,
                )
              : views,
          );
          taskViewsCacheRef.current.update(server);
        },
        removeView: removeViewLocally,
        setSaveState: (taskViewId, state) => setSaveState(buildViewKey(taskViewId), state),
        notifySaveFailed: (retry) => {
          // Alias so the translation scanner sees bare t calls.
          const t = tRef.current;

          appContextRef.current.showNotification(
            t('tasks:TaskViews.Errors.SaveViewFailed'),
            true,
            false,
            false,
            { text: t('tasks:TaskViews.Errors.SaveViewRetry'), onClick: retry },
          );
        },
      }),
  );

  useEffect(() => {
    const saveStateTimers = saveStateTimersRef.current;

    return () => {
      saveQueue.clear();
      saveStateTimers.forEach(clearTimeout);
    };
  }, [saveQueue]);

  const resetViewSaves = useCallback(() => {
    saveQueue.clear();
    deletedViewIdsRef.current.clear();
    saveStateTimersRef.current.forEach(clearTimeout);
    saveStateTimersRef.current.clear();
    setSaveStates({});
  }, [saveQueue]);

  const editView = useCallback(
    (viewKey: string, fields: TaskViewEditableFields) => {
      const view = customViewsRef.current.find((v) => v.key === viewKey);
      if (!view) return;
      if (view.taskViewId && view.ownedByMe === false) {
        // Someone else's shared view is never written; the scene makes a personal copy instead.
        logTaskViewDiagnostic('view.edit.ignoredNotOwned', { viewKey });

        return;
      }

      const label = fields.label ?? view.label;
      const filterValues = fields.filterValues ? [...fields.filterValues] : view.filterValues;
      commitCustomViews((views) =>
        views.map((v) =>
          v.key === viewKey ? { ...v, label, filterValues, url: getCustomViewUrl(getCustomViewRouteId(v), label) } : v,
        ),
      );
      if (view.taskViewId) {
        saveQueue.edit(view.taskViewId, fields);
      } else if (fields.filterValues !== undefined) {
        // A draft has no row yet; createView sends these values.
        setSaveState(viewKey, 'saved');
      }
    },
    [commitCustomViews, saveQueue, setSaveState],
  );

  const flushView = useCallback(
    async (viewKey: string): Promise<void> => {
      const taskViewId = findTaskViewId(customViewsRef.current, viewKey);
      if (taskViewId) await saveQueue.flush(taskViewId);
    },
    [saveQueue],
  );

  const retryView = useCallback(
    (viewKey: string) => {
      const taskViewId = findTaskViewId(customViewsRef.current, viewKey);
      if (taskViewId) saveQueue.retry(taskViewId);
    },
    [saveQueue],
  );

  // A freshly created row (POST, copy, or a view created in another tab).
  const adoptCreatedView = useCallback(
    (created: TaskViewDTO, replaceDraftKey?: string): ITaskCustomView => {
      const view = toCustomView(saveQueue.track(created), saveQueue.edits(created.taskViewId), isManager);
      commitCustomViews((views) => {
        if (views.some((v) => v.key === replaceDraftKey)) {
          return views.filter((v) => v.key !== view.key).map((v) => (v.key === replaceDraftKey ? view : v));
        }

        // The hub event can arrive before the POST response.
        return views.some((v) => v.key === view.key) ? views : [...views, view];
      });
      const cache = taskViewsCacheRef.current;
      if (cache.has(created.taskViewId)) {
        cache.update(created);
      } else {
        cache.add(created);
      }

      return customViewsRef.current.find((v) => v.key === view.key) ?? view;
    },
    [commitCustomViews, isManager, saveQueue],
  );

  const createView = useCallback(
    async (input: ICreateTaskViewInput): Promise<ITaskCustomView | undefined> => {
      // A response that lands after a tenant/user switch must not be adopted
      // into the new scope's state and cache; the row exists in the old tenant.
      const requestScopeKey = currentScopeKeyRef.current;
      const accessToken = await appContextRef.current.getAccessToken(apiRequest.scopes);
      const created = await apiCreateTaskView(
        {
          name: input.name,
          filterValues: input.filterValues,
          sourceRoute: input.sourceRoute,
          parentViewId: input.parentViewId ?? null,
        },
        accessToken,
      );
      if (currentScopeKeyRef.current !== requestScopeKey) {
        logTaskViewDiagnostic('state.createView.ignoreStaleScope', {
          requestScopeKey,
          currentScopeKey: currentScopeKeyRef.current,
        });

        return undefined;
      }

      return adoptCreatedView(created, input.replaceDraftKey);
    },
    [adoptCreatedView],
  );

  const copyView = useCallback(
    async (source: CopyTaskViewSource): Promise<ITaskCustomView | undefined> => {
      const dto = new TaskViewCopyDTO();
      if ('viewKey' in source) {
        const sourceView = customViewsRef.current.find((v) => v.key === source.viewKey);
        if (!sourceView?.taskViewId) return undefined;
        dto.sourceTaskViewId = sourceView.taskViewId;
        // The label the client shows, including a rename whose save is still
        // pending; the server names the copy from it instead of the stored name.
        dto.name = sourceView.label;
      } else {
        dto.name = source.templateLabel;
      }
      dto.filterValues = source.filterValues ? [...source.filterValues] : null;
      dto.sourceRoute = source.sourceRoute ?? null;
      // Same stale-scope rule as createView.
      const requestScopeKey = currentScopeKeyRef.current;
      const accessToken = await appContextRef.current.getAccessToken(apiRequest.scopes);
      const created = await apiCopyTaskView(dto, accessToken);
      if (currentScopeKeyRef.current !== requestScopeKey) {
        logTaskViewDiagnostic('state.copyView.ignoreStaleScope', {
          requestScopeKey,
          currentScopeKey: currentScopeKeyRef.current,
        });

        return undefined;
      }

      return adoptCreatedView(created);
    },
    [adoptCreatedView],
  );

  // Sharing changes the revision, so it queues behind a running save
  // and handles a 409 the same way.
  const shareView = useCallback(
    async (viewKey: string, isShared: boolean): Promise<ITaskCustomView | undefined> => {
      const taskViewId = findTaskViewId(customViewsRef.current, viewKey);
      if (!taskViewId || !saveQueue.isTracked(taskViewId)) return undefined;
      await saveQueue.share(taskViewId, isShared);

      return customViewsRef.current.find((v) => v.taskViewId === taskViewId);
    },
    [saveQueue],
  );

  const deleteView = useCallback(
    async (viewKey: string): Promise<void> => {
      const view = customViewsRef.current.find((v) => v.key === viewKey);
      if (!view) return;
      if (!view.taskViewId) {
        commitCustomViews((views) => views.filter((v) => v.key !== viewKey));

        return;
      }

      // Optimistic: the view leaves the UI first. A running save for it drops its result, and
      // the hub echo is skipped because the id is in deletedViewIdsRef. The removal is
      // unconfirmed until the DELETE lands, so no load may un-suppress it yet.
      const { taskViewId } = view;
      saveQueue.forget(taskViewId);
      removeViewLocally(taskViewId, false);
      try {
        const accessToken = await appContextRef.current.getAccessToken(apiRequest.scopes);
        await apiDeleteTaskView(taskViewId, accessToken);
        deletedViewIdsRef.current.set(taskViewId, Date.now());
      } catch (err) {
        // The row is still there; the caller's reload brings it back.
        deletedViewIdsRef.current.delete(taskViewId);
        throw AppError.fromApiError(err);
      }
    },
    [commitCustomViews, removeViewLocally, saveQueue],
  );

  const addDraftView = useCallback(
    (view: ITaskCustomView) => {
      commitCustomViews((views) => (views.some((v) => v.key === view.key) ? views : [...views, view]));
    },
    [commitCustomViews],
  );

  const removeDraftView = useCallback(
    (viewKey: string) => {
      commitCustomViews((views) => (views.some((v) => v.key === viewKey) ? views.filter((v) => v.key !== viewKey) : views));
    },
    [commitCustomViews],
  );

  const { loadTaskViewLayout, applyLayoutFromEvent, saveTaskViewLayoutNow } = useTaskViewLayoutSync({
    appContext,
    isLoading: isLayoutLoading,
    layoutStateScopeKey: scopeKey,
    layoutDoc,
    layoutDirty: layoutState.dirty,
    layoutMutationSeq: layoutState.mutationSeq,
    applyRemoteLayout,
    markLayoutSaved,
    onLayoutSaveStateChange: setLayoutSaveState,
  });

  const resetTaskViewLayout = useCallback(
    async (layout: ITaskViewLayoutState, selectedViewKey: string | undefined): Promise<boolean> => {
      applyRemoteLayout(layout);
      setSelectedTaskViewKey(selectedViewKey);

      return saveTaskViewLayoutNow(layout);
    },
    [applyRemoteLayout, saveTaskViewLayoutNow, setSelectedTaskViewKey],
  );

  const loadTaskViews = useCallback(
    async (refresh: boolean = false, reason: string = 'unspecified') => {
      const requestScopeKey = currentScopeKeyRef.current;
      logTaskViewDiagnostic('state.loadTaskViews.start', {
        reason,
        refresh,
        requestScopeKey,
        isManager,
      });

      // A view created or copied while the GET is out is missing from the list and must survive it.
      const idsBeforeLoad = new Set(saveQueue.trackedIds());
      const loadStartedAt = Date.now();
      try {
        const cachedViews = await taskViewsCacheRef.current.getItems(refresh);
        logTaskViewDiagnostic('state.loadTaskViews.cacheReturned', {
          reason,
          refresh,
          count: cachedViews.length,
        });
        if (currentScopeKeyRef.current !== requestScopeKey) {
          logTaskViewDiagnostic('state.loadTaskViews.ignoreStaleScope', {
            reason,
            requestScopeKey,
            currentScopeKey: currentScopeKeyRef.current,
          });

          return;
        }

        // A view this tab removed stays out until a list confirms it is gone;
        // the cache is trimmed with it so getItems(false) agrees. One
        // exception: a load that STARTED after a server-confirmed removal and
        // still lists the row is server truth -- the row exists again (an
        // unshared view was re-shared) and the suppression is lifted.
        const deletedViewIds = deletedViewIdsRef.current;
        const cache = taskViewsCacheRef.current;
        deletedViewIds.forEach((removedAt, taskViewId) => {
          if (!cachedViews.some((view) => view.taskViewId === taskViewId)) {
            deletedViewIds.delete(taskViewId);

            return;
          }
          if (loadStartedAt > removedAt) {
            logTaskViewDiagnostic('state.loadTaskViews.unsuppressRelisted', { taskViewId });
            deletedViewIds.delete(taskViewId);

            return;
          }
          if (cache.has(taskViewId)) cache.remove(cache.get(taskViewId));
        });
        const apiViews = cachedViews.filter(
          (view) => !deletedViewIds.has(view.taskViewId) && isViewVisible(view, isManager),
        );
        logTaskViewDiagnostic('state.loadTaskViews.filtered', {
          reason,
          cachedCount: cachedViews.length,
          apiViewCount: apiViews.length,
          apiViewKeys: apiViews.map((view) => `view-api-${view.taskViewId}`),
        });

        // Newly-arrived shared-by-others views must not auto-appear in the tab
        // bar, and any custom view starts hidden from the left nav until the
        // user opts in. Apply both hidden-key additions in one layout edit so
        // the save-effect never observes a half-applied layout. Views the user
        // already knows about (in customViews or already hidden) are left alone;
        // the server layout (loadTaskViewLayout) stays authoritative for those.
        dispatchLayout({
          type: 'EDIT',
          update: (state) => {
            const knownTabBarKeys = new Set<string>([
              ...customViewsRef.current.map((v) => v.key),
              ...state.hiddenCustomViewKeys,
            ]);

            const knownSidebarKeys = new Set<string>([
              ...customViewsRef.current.map((v) => v.key),
              ...state.hiddenSidebarViewKeys,
            ]);
            const tabBarAdditions: string[] = [];
            const sidebarAdditions: string[] = [];
            apiViews.forEach((view) => {
              const key = `view-api-${view.taskViewId}`;
              if (view.isShared && view.ownedByMe === false && !knownTabBarKeys.has(key)) {
                tabBarAdditions.push(key);
              }
              if (!knownSidebarKeys.has(key)) {
                sidebarAdditions.push(key);
              }
            });

            if (!tabBarAdditions.length && !sidebarAdditions.length) {
              return state;
            }

            return {
              ...state,
              hiddenCustomViewKeys: tabBarAdditions.length
                ? [...state.hiddenCustomViewKeys, ...tabBarAdditions]
                : state.hiddenCustomViewKeys,
              hiddenSidebarViewKeys: sidebarAdditions.length
                ? [...state.hiddenSidebarViewKeys, ...sidebarAdditions]
                : state.hiddenSidebarViewKeys,
            };
          },
        });

        // The server is authoritative for everything this tab has not edited: a
        // resync (reconnect, tab visible again, the view chooser) must not revert
        // an edit that waits to be saved.
        const serverIds = new Set(apiViews.map((dto) => dto.taskViewId));
        const forgotten = [...idsBeforeLoad].filter((id) => !serverIds.has(id));
        forgotten.forEach((id) => saveQueue.forget(id));
        const isCreatedDuringLoad = (id: number | undefined): boolean =>
          id !== undefined && !serverIds.has(id) && !idsBeforeLoad.has(id) && saveQueue.isTracked(id);

        const fromServer = apiViews.map((dto) =>
          toCustomView(saveQueue.track(dto), saveQueue.edits(dto.taskViewId), isManager),
        );
        // Local drafts, plus views created while the GET was out.
        const keptLocal = customViewsRef.current.filter(
          (v) => v.key.startsWith(draftKeyPrefix) || isCreatedDuringLoad(v.taskViewId),
        );

        // A refresh replaces the cache with the fetched list; add the views created during the load back.
        keptLocal.forEach((v) => {
          const server = v.taskViewId ? saveQueue.serverVersion(v.taskViewId) : undefined;
          if (server && !cache.has(server.taskViewId)) cache.add(server);
        });

        logTaskViewDiagnostic('state.loadTaskViews.merged', {
          reason,
          serverCount: fromServer.length,
          forgotten,
          keptLocal: keptLocal.map((v) => v.key),
        });
        commitCustomViews(() => sanitizeCustomViews([...fromServer, ...keptLocal], isManager));
      } catch {
        logTaskViewDiagnostic('state.loadTaskViews.error', { reason, refresh });
        // API failure -- keep the current in-memory state.
      } finally {
        logTaskViewDiagnostic('state.loadTaskViews.finally', { reason, refresh });
      }
    },
    [isManager, commitCustomViews, saveQueue],
  );

  const { subscribe: subscribeToTaskViewHub } = useTaskViewHub();

  // Mount + scope-change load. On a genuine tenant/user scope change the atoms
  // are reset to defaults first so the previous scope's views never flash under
  // the new one. Pre-login or unauthorized states have nothing to load.
  const lastLoadedScopeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    let active = true;

    if (!taskViewsAvailable) {
      logTaskViewDiagnostic('state.mount.skipNotAuthorized', {
        scopeKey,
        isAuthenticated: appContext.isAuthenticated,
        isAuthInProgress: appContext.isAuthInProgress,
        tenantId: appContext.user?.login?.tenantId,
      });
      lastLoadedScopeRef.current = undefined;
      applyRemoteLayout(createTaskViewLayoutState());
      resetViewSaves();
      customViewsRef.current = [];
      setProviderState({ ...createInitialProviderState(), isLayoutLoading: false });

      return () => {
        active = false;
      };
    }

    const isScopeChange = lastLoadedScopeRef.current !== undefined && lastLoadedScopeRef.current !== scopeKey;
    if (isScopeChange) {
      logTaskViewDiagnostic('state.mount.scopeChangedReset', {
        previousScopeKey: lastLoadedScopeRef.current,
        nextScopeKey: scopeKey,
      });
      applyRemoteLayout(createTaskViewLayoutState());
      // A save queued for the previous tenant/user must not run under the new
      // one -- same rule as the layout sync's scope reset.
      resetViewSaves();
      customViewsRef.current = [];
    }
    lastLoadedScopeRef.current = scopeKey;
    setProviderState((state) => ({
      customViews: isScopeChange ? [] : state.customViews,
      selectedTaskViewKey: isScopeChange ? undefined : state.selectedTaskViewKey,
      isLayoutLoading: true,
    }));
    logTaskViewDiagnostic('state.mount.loadStart', { scopeKey });

    const load = async (): Promise<void> => {
      try {
        await loadTaskViews(true, 'provider.mount');
        await loadTaskViewLayout('provider.mount');
      } finally {
        if (active) {
          logTaskViewDiagnostic('state.mount.loadDone', { scopeKey });
          setIsLayoutLoading(false);
        } else {
          logTaskViewDiagnostic('state.mount.loadDoneInactive', { scopeKey });
        }
      }
    };
    void load();

    return () => {
      active = false;
    };
  }, [
    appContext.user?.login?.tenantId,
    appContext.isAuthenticated,
    appContext.isAuthInProgress,
    taskViewsAvailable,
    scopeKey,
    loadTaskViews,
    loadTaskViewLayout,
    applyRemoteLayout,
    setIsLayoutLoading,
    resetViewSaves,
  ]);

  // SignalR cross-tab sync. Live viewMutated / viewDeleted events carry what
  // they need and are applied directly; only a resync refetches. Refetch
  // coalescing -- when resyncs arrive faster than a fetch can settle the
  // in-flight fetch absorbs the burst and one trailing fetch runs after it
  // lands. Prevents N triggers => N GETs.
  const viewsFetchInflightRef = useRef(false);
  const viewsFetchQueuedRef = useRef(false);
  const viewsFetchQueuedRefreshRef = useRef(false);

  useEffect(() => {
    if (!taskViewsAvailable) {
      logTaskViewDiagnostic('state.hub.skipSubscribeNotAuthorized', { scopeKey });

      return;
    }

    const coalescedLoadTaskViews = async (refresh: boolean, reason: string): Promise<void> => {
      if (viewsFetchInflightRef.current) {
        viewsFetchQueuedRef.current = true;
        viewsFetchQueuedRefreshRef.current = viewsFetchQueuedRefreshRef.current || refresh;
        logTaskViewDiagnostic('state.hub.loadTaskViews.queued');

        return;
      }
      viewsFetchInflightRef.current = true;
      logTaskViewDiagnostic('state.hub.loadTaskViews.start', { refresh, reason });
      try {
        await loadTaskViews(refresh, reason);
      } finally {
        viewsFetchInflightRef.current = false;
        logTaskViewDiagnostic('state.hub.loadTaskViews.done', {
          queued: viewsFetchQueuedRef.current,
        });
      }
      if (viewsFetchQueuedRef.current) {
        const queuedRefresh = viewsFetchQueuedRefreshRef.current;
        viewsFetchQueuedRef.current = false;
        viewsFetchQueuedRefreshRef.current = false;
        logTaskViewDiagnostic('state.hub.loadTaskViews.runQueued', {
          refresh: queuedRefresh,
          reason,
        });
        await coalescedLoadTaskViews(queuedRefresh, `${reason}:queued`);
      }
    };

    const handleEvent: TaskViewEventHandler = (evt) => {
      logTaskViewDiagnostic('state.hub.event', {
        kind: evt.kind,
        sequenceNumber: evt.sequenceNumber,
      });
      switch (evt.kind) {
        case 'viewMutated': {
          // The event carries the whole view, so no GET is needed.
          const dto = evt.view;
          if (deletedViewIdsRef.current.has(dto.taskViewId)) {
            logTaskViewDiagnostic('state.hub.viewMutated.skipDeleted', { taskViewId: dto.taskViewId });
            break;
          }
          if (saveQueue.isTracked(dto.taskViewId)) {
            saveQueue.receive(dto);
            break;
          }
          // An own view created in another tab; the server sends viewMutated to the owner only.
          // Its layoutMutated event brings the tab-bar placement.
          if (isViewVisible(dto, isManager)) {
            adoptCreatedView(dto);
            logTaskViewDiagnostic('state.hub.viewMutated.added', { taskViewId: dto.taskViewId });
          }
          break;
        }
        case 'viewDeleted': {
          saveQueue.forget(evt.taskViewId);
          removeViewLocally(evt.taskViewId);
          break;
        }
        case 'layoutMutated': {
          // The event carries the full layout -- apply it directly, no GET.
          // applyLayoutFromEvent skips this tab's own echo and keeps any
          // unsaved local edits.
          applyLayoutFromEvent(evt.layout);
          break;
        }
      }
    };

    const handleResync: TaskViewResyncHandler = async () => {
      logTaskViewDiagnostic('state.hub.resync.start');
      await coalescedLoadTaskViews(true, 'hub.resync');
      await loadTaskViewLayout('hub.resync');
      logTaskViewDiagnostic('state.hub.resync.done');
    };

    return subscribeToTaskViewHub(handleEvent, handleResync);
  }, [
    appContext.user?.login?.tenantId,
    taskViewsAvailable,
    scopeKey,
    isManager,
    subscribeToTaskViewHub,
    loadTaskViews,
    loadTaskViewLayout,
    applyLayoutFromEvent,
    adoptCreatedView,
    saveQueue,
    removeViewLocally,
  ]);

  const value = useMemo<ITaskViewStateContextValue>(
    () => ({
      scopeKey,
      isLayoutLoading,
      layoutSaveState: saveStates[layoutSaveStateKey] ?? 'idle',
      customViews,
      viewOrder,
      hiddenTemplateViewKeys,
      hiddenCustomViewKeys,
      hiddenSidebarViewKeys,
      selectedTaskViewKey,
      viewMemberFilters,
      standardViewFilterOverrides,
      viewSaveStates: saveStates,
      editView,
      flushView,
      retryView,
      createView,
      copyView,
      shareView,
      deleteView,
      addDraftView,
      removeDraftView,
      setSelectedTaskViewKey,
      replaceViewOrder,
      pruneLayoutKeys,
      showViewInTabBar,
      showCreatedCustomViewInTabBar,
      hideViewEverywhere,
      hideManyViewsEverywhere,
      setViewSidebarVisibility,
      setManyViewsSidebarVisibility,
      removeViewFromLayout,
      resetTaskViewLayout,
      setViewMemberFilter,
      setStandardViewFilterOverride,
      loadTaskViews,
      loadTaskViewLayout,
    }),
    [
      scopeKey,
      isLayoutLoading,
      saveStates,
      customViews,
      viewOrder,
      hiddenTemplateViewKeys,
      hiddenCustomViewKeys,
      hiddenSidebarViewKeys,
      selectedTaskViewKey,
      viewMemberFilters,
      standardViewFilterOverrides,
      editView,
      flushView,
      retryView,
      createView,
      copyView,
      shareView,
      deleteView,
      addDraftView,
      removeDraftView,
      setSelectedTaskViewKey,
      replaceViewOrder,
      pruneLayoutKeys,
      showViewInTabBar,
      showCreatedCustomViewInTabBar,
      hideViewEverywhere,
      hideManyViewsEverywhere,
      setViewSidebarVisibility,
      setManyViewsSidebarVisibility,
      removeViewFromLayout,
      resetTaskViewLayout,
      setViewMemberFilter,
      setStandardViewFilterOverride,
      loadTaskViews,
      loadTaskViewLayout,
    ],
  );

  return <TaskViewStateContext.Provider value={value}>{children}</TaskViewStateContext.Provider>;
};
